// Screens: 18-holder-subscription-detail (holder view), 03-member-subscription-detail (W10)
import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { HolderSummaryCard } from '@/entities/subscription/HolderSummaryCard';
import { MemberRow } from '@/entities/subscription/MemberRow';
import { InviteLinkCard } from '@/entities/subscription/InviteLinkCard';
import { subscriptionApi } from '@/entities/subscription/api';
import { ledgerApi } from '@/entities/ledger-operation/api';
import { OperationRow } from '@/entities/ledger-operation/OperationRow';
import { EventRow } from '@/entities/ledger-operation/EventRow';
import { MarkPaidSheet } from '@/features/mark-paid/MarkPaidSheet';
import type { Subscription } from '@/entities/subscription/types';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';
import { amountText, toNumber } from '@/shared/lib/money';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusBadgeVariant(status: Subscription['status']) {
  switch (status) {
    case 'active':  return 'active'  as const;
    case 'draft':   return 'draft'   as const;
    case 'paused':  return 'due'     as const;
    case 'closing': return 'confirm' as const;
    default:        return 'draft'   as const;
  }
}

function statusBadgeLabel(status: Subscription['status'], t: ReturnType<typeof useT>) {
  switch (status) {
    case 'active':  return t('subscription.status.active');
    case 'draft':   return t('subscription.status.draft');
    case 'paused':  return t('subscription.status.paused');
    case 'closing': return t('subscription.status.closing');
    case 'closed':  return t('subscription.status.closed');
    default:        return status;
  }
}

// ─── Icon in header ───────────────────────────────────────────────────────────

function SubIcon({ sub }: { sub: Subscription }) {
  const letter = sub.name.trim()[0]?.toUpperCase() ?? '?';
  const isEmoji = sub.icon.length > 0 && !/^[a-z0-9][a-z0-9-]*$/.test(sub.icon);
  const isSlug  = sub.icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(sub.icon);

  if (isEmoji) {
    return (
      <span className="w-[34px] h-[34px] rounded-[10px] bg-brand-gradient flex items-center justify-center text-[18px] shadow-[0_3px_8px_rgba(18,120,90,0.28)] flex-shrink-0 leading-none">
        {sub.icon}
      </span>
    );
  }
  if (isSlug) {
    return (
      <img
        src={`/api/v1/icons/${sub.icon}/default`}
        alt={sub.name}
        className="w-[34px] h-[34px] rounded-[10px] object-contain bg-white/60 border border-white/60 p-1 flex-shrink-0"
      />
    );
  }
  return <SubscriptionIcon letter={letter} size="md" />;
}

// ─── Action chips ─────────────────────────────────────────────────────────────

function ChipRow({ subscriptionId, status }: { subscriptionId: string; status: Subscription['status'] }) {
  const navigate = useNavigate();
  const t = useT();
  const [moreOpen, setMoreOpen] = useState(false);
  const canClose = status === 'active' || status === 'paused';

  const remindM = useMutation({
    mutationFn: () => subscriptionApi.remind(subscriptionId),
  });

  const chips = [
    {
      label: t('subscription.actions.remindAll'),
      onClick: () => remindM.mutate(),
    },
    { label: t('subscription.actions.changePrice'), onClick: () => navigate(`/subscriptions/${subscriptionId}/change-price`) },
    { label: t('subscription.actions.addMember'), onClick: () => navigate(`/subscriptions/${subscriptionId}/add-member`) },
    { label: t('subscription.actions.more'), onClick: () => setMoreOpen(true) },
  ];

  return (
    <>
      <div className="overflow-x-auto -mx-[18px] px-[18px] [&::-webkit-scrollbar]:hidden">
        <div className="flex gap-2 min-w-max">
          {chips.map((c) => (
            <button
              key={c.label}
              onClick={c.onClick}
              className="whitespace-nowrap text-[13px] font-medium rounded-full px-[14px] py-[8px] bg-[rgba(244,251,245,0.55)] border border-white/60 active:opacity-70 transition-opacity"
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      {remindM.isSuccess && (
        <p className="text-[12px] text-text-secondary text-center mt-1">
          {remindM.data.notified_count > 0
            ? t('remind.sent', { count: remindM.data.notified_count })
            : t('remind.noDebtors')}
        </p>
      )}
      {remindM.isError && (
        <p className="text-[12px] text-status-debt text-center mt-1">{t('remind.error')}</p>
      )}
      {moreOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-end justify-center"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-t-[20px] p-4 flex flex-col gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="text-left rounded-[12px] px-4 py-3 text-[15px] bg-white/50 border border-white/60"
              onClick={() => { setMoreOpen(false); navigate(`/subscriptions/${subscriptionId}/settings`); }}
            >
              {t('subscription.actions.settings')}
            </button>
            {canClose && (
              <button
                className="text-left rounded-[12px] px-4 py-3 text-[15px] text-status-debt bg-white/50 border border-white/60"
                onClick={() => { setMoreOpen(false); navigate(`/subscriptions/${subscriptionId}/close`); }}
              >
                {t('subscription.actions.close')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Tab switcher ─────────────────────────────────────────────────────────────

type Tab = 'members' | 'finance' | 'events';
type MemberTab = 'operations' | 'history';

function normalizeTab(value: string | null): Tab {
  return value === 'finance' || value === 'events' ? value : 'members';
}

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  const t = useT();
  const tabs: { key: Tab; label: string }[] = [
    { key: 'members', label: t('subscription.tabs.members') },
    { key: 'finance', label: t('subscription.tabs.finance') },
    { key: 'events',  label: t('subscription.tabs.events') },
  ];
  return (
    <div className="flex p-[4px] rounded-[14px] bg-[rgba(240,250,242,0.4)] border border-white/50 text-[13px]">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={cn(
            'flex-1 text-center font-semibold py-[7px] rounded-[11px] transition-all',
            active === t.key
              ? 'bg-white/85 shadow-[0_4px_10px_rgba(18,52,36,0.08)] text-text-primary'
              : 'text-text-secondary',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function MemberTabBar({ active, onChange }: { active: MemberTab; onChange: (t: MemberTab) => void }) {
  const t = useT();
  const tabs: { key: MemberTab; label: string }[] = [
    { key: 'operations', label: t('subscription.memberDetail.operations') },
    { key: 'history', label: t('subscription.memberDetail.history') },
  ];
  return (
    <div className="flex p-[4px] rounded-[14px] bg-[rgba(240,250,242,0.4)] border border-white/50 text-[14px]">
      {tabs.map((item) => (
        <button
          key={item.key}
          onClick={() => onChange(item.key)}
          className={cn(
            'flex-1 text-center font-semibold py-[8px] rounded-[11px] transition-all',
            active === item.key
              ? 'bg-white/85 shadow-[0_4px_10px_rgba(18,52,36,0.08)] text-text-primary'
              : 'text-text-secondary',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="h-[80px] rounded-[22px] bg-white/40" />
      <div className="h-[40px] rounded-[12px] bg-white/30" />
      <div className="h-[48px] rounded-[14px] bg-white/30" />
      <div className="h-[44px] rounded-[14px] bg-white/30" />
      <div className="h-[44px] rounded-[14px] bg-white/30" />
    </div>
  );
}

function ListPanel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[20px] px-4 py-1 bg-[rgba(244,251,245,0.5)] border border-white/55 shadow-[0_8px_22px_rgba(18,52,36,0.08)] backdrop-blur-[8px]">
      {children}
    </div>
  );
}

function InlineState({ text, onRetry }: { text: string; onRetry?: () => void }) {
  const t = useT();
  return (
    <div className="py-8 text-center text-[14px] text-text-secondary">
      <p>{text}</p>
      {onRetry && (
        <button className="mt-3 text-brand-green font-semibold" onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

function daysLeftLabel(sub: Subscription, t: ReturnType<typeof useT>) {
  const value = sub.deadline ?? sub.next_billing_date ?? sub.billing_date;
  if (!value) return t('common.dash');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('common.dash');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const days = Math.ceil((date.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return t('common.overdue');
  if (days === 0) return t('common.today');
  if (days === 1) return t('common.tomorrow');
  return t('common.daysShort', { count: days });
}

function BalanceCard({ sub, balance }: { sub: Subscription; balance: number }) {
  const t = useT();
  const isDebt = balance > 0;
  const progress = isDebt ? 232 : 360;

  return (
    <div className="rounded-[24px] p-5 flex items-center gap-4 glass-hero">
      <div
        className="w-[96px] h-[96px] rounded-full flex items-center justify-center shadow-[0_8px_20px_rgba(18,120,90,0.25)] flex-shrink-0"
        style={{
          background: `conic-gradient(#54992B 0deg, #0C9981 ${progress}deg, rgba(255,255,255,0.42) ${progress}deg)`,
        }}
      >
        <div className="w-[74px] h-[74px] rounded-full bg-white/80 backdrop-blur-[4px] flex flex-col items-center justify-center leading-none">
          <span className="text-[18px] font-bold text-center px-1">{daysLeftLabel(sub, t)}</span>
          <span className="text-[10px] text-text-secondary mt-1">{t('subscription.memberDetail.left')}</span>
        </div>
      </div>
      <div className="min-w-0">
        <p className="text-[13px] text-text-secondary">{t('subscription.memberDetail.balance')}</p>
        <p className={cn('text-[36px] font-bold font-mono leading-[1.05]', isDebt ? 'text-status-debt' : 'text-status-paid')}>
          {isDebt ? '-' : ''}{amountText(balance, sub.currency)}
        </p>
        <p className="text-[13px] text-text-secondary mt-1">
          {sub.billing_date
            ? t('subscription.memberDetail.due', { date: new Date(sub.billing_date).toLocaleDateString() })
            : t('common.currentPeriod')}
        </p>
        <span className="inline-flex items-center mt-2 text-[11px] font-semibold text-status-credit bg-[rgba(12,143,128,0.1)] border border-[rgba(12,143,128,0.24)] rounded-full px-3 py-1">
          {t('subscription.memberDetail.private')}
        </span>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const t = useT();
  const [tab, setTabState] = useState<Tab>(() => normalizeTab(searchParams.get('tab')));
  const [memberTab, setMemberTab] = useState<MemberTab>(() => searchParams.get('tab') === 'history' ? 'history' : 'operations');
  const [payOpen, setPayOpen] = useState(false);

  function setTab(nextTab: Tab) {
    setTabState(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'members') next.delete('tab');
    else next.set('tab', nextTab);
    setSearchParams(next, { replace: true });
  }

  const detailQ = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => subscriptionApi.getDetail(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (detailQ.data?.is_holder && detailQ.data.subscription.status === 'draft') {
      navigate(`/create?draft=${id}`, { replace: true });
    }
  }, [detailQ.data, id, navigate]);

  const membersQ = useQuery({
    queryKey: ['subscription-members', id],
    queryFn: () => subscriptionApi.getMembers(id!),
    enabled: !!id,
  });

  const inviteQ = useQuery({
    queryKey: ['subscription-invite', id],
    queryFn: () => subscriptionApi.getInvite(id!),
    enabled: !!id,
  });

  const operationsQ = useQuery({
    queryKey: ['subscription-operations', id],
    queryFn: () => ledgerApi.listOperations(id!, { limit: 40 }),
    enabled: !!id && tab === 'finance',
  });

  const historyQ = useQuery({
    queryKey: ['subscription-history', id],
    queryFn: () => ledgerApi.listHistory(id!, { limit: 40 }),
    enabled: !!id && tab === 'events',
  });

  const memberOperationsQ = useQuery({
    queryKey: ['subscription-operations', id, 'viewer'],
    queryFn: () => ledgerApi.listOperations(id!, { limit: 40 }),
    enabled: !!id && detailQ.data?.is_holder === false && memberTab === 'operations',
  });

  const memberHistoryQ = useQuery({
    queryKey: ['subscription-history', id, 'viewer'],
    queryFn: () => ledgerApi.listHistory(id!, { limit: 40 }),
    enabled: !!id && detailQ.data?.is_holder === false && memberTab === 'history',
  });

  const requestExitM = useMutation({
    mutationFn: () => subscriptionApi.requestExit(id!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription', id] });
      await qc.invalidateQueries({ queryKey: ['subscription-history', id] });
    },
  });

  if (detailQ.isLoading) {
    return (
      <Page>
        <PageHeader title={<span className="font-bold text-[19px]">{t('subscription.title')}</span>} />
        <div className="px-[18px] pt-2"><Skeleton /></div>
      </Page>
    );
  }

  if (detailQ.isError || !detailQ.data) {
    return (
      <Page>
        <PageHeader title={<span className="font-bold text-[19px]">{t('subscription.title')}</span>} />
        <div className="px-[18px] pt-8 text-center text-text-secondary">
          <p>{t('subscription.loadError')}</p>
          <button className="mt-3 text-brand-green font-semibold" onClick={() => detailQ.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      </Page>
    );
  }

  const { subscription: sub, is_holder } = detailQ.data;
  const members = membersQ.data?.items ?? [];
  const invite = inviteQ.data ?? null;
  const operations = operationsQ.data?.items ?? [];
  const events = historyQ.data?.items ?? [];
  const memberOperations = memberOperationsQ.data?.items ?? [];
  const memberEvents = memberHistoryQ.data?.items ?? [];
  const isDraft = sub.status === 'draft';
  const viewerDebt = isDraft
    ? 0
    : Math.max(
        0,
        toNumber(detailQ.data.viewer_balance) ?? Number(sub.total_price) / Math.max(1, sub.member_count || 1),
      );

  if (!is_holder) {
    const isArchived = sub.status === 'closed' || sub.status === 'archived';
    const canAct = !isArchived && !isDraft;
    return (
      <>
        <Page>
          <PageHeader
            title={
              <>
                <SubIcon sub={sub} />
                <b className="text-[19px] tracking-[-0.01em] truncate">{sub.name}</b>
              </>
            }
            right={
              <StatusBadge
                variant={statusBadgeVariant(sub.status)}
                label={statusBadgeLabel(sub.status, t)}
              />
            }
          />

          <div className="px-[18px] pb-8 flex flex-col gap-[13px]">
            <BalanceCard sub={sub} balance={viewerDebt} />

            {isArchived && (
              <div className="rounded-[14px] p-3 text-[13px] text-[#1c5a4a] bg-[rgba(240,250,242,0.5)] border border-white/60">
                {t('subscription.memberDetail.finalInvoice')}
              </div>
            )}

            {isDraft && (
              <div className="rounded-[14px] p-3 text-[13px] text-[#1c5a4a] bg-[rgba(240,250,242,0.5)] border border-white/60">
                {t('subscription.memberDetail.draftHint')}
              </div>
            )}

            {canAct && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="text-[17px] font-semibold text-white bg-brand-gradient rounded-[16px] py-[15px] shadow-[0_12px_26px_rgba(18,120,90,0.38)]"
                  onClick={() => setPayOpen(true)}
                >
                  {t('subscription.memberCard.iPaid')}
                </button>
                <button
                  className="text-[17px] font-semibold text-text-primary rounded-[16px] py-[15px] bg-[rgba(244,251,245,0.55)] border border-white/60"
                  onClick={() => setPayOpen(true)}
                >
                  {t('payment.otherAmount')}
                </button>
              </div>
            )}

            <MemberTabBar active={memberTab} onChange={setMemberTab} />

            {memberTab === 'operations' && (
              <div>
                {memberOperationsQ.isLoading && <InlineState text={t('operation.loading')} />}
                {memberOperationsQ.isError && (
                  <InlineState text={t('operation.loadError')} onRetry={() => { void memberOperationsQ.refetch(); }} />
                )}
                {!memberOperationsQ.isLoading && !memberOperationsQ.isError && memberOperations.length === 0 && (
                  <InlineState text={t('operation.empty')} />
                )}
                {memberOperations.length > 0 && (
                  <ListPanel>
                    {memberOperations.map((op) => <OperationRow key={op.id} op={op} />)}
                  </ListPanel>
                )}
              </div>
            )}

            {memberTab === 'history' && (
              <div className="flex flex-col gap-2">
                {memberHistoryQ.isLoading && <InlineState text={t('event.loading')} />}
                {memberHistoryQ.isError && (
                  <InlineState text={t('event.loadError')} onRetry={() => { void memberHistoryQ.refetch(); }} />
                )}
                {!memberHistoryQ.isLoading && !memberHistoryQ.isError && memberEvents.length === 0 && (
                  <InlineState text={t('event.empty')} />
                )}
                {memberEvents.length > 0 && (
                  <ListPanel>
                    {memberEvents.map((event) => <EventRow key={event.id} event={event} />)}
                  </ListPanel>
                )}
                <p className="text-[13px] text-status-credit rounded-[14px] p-3 text-center bg-[rgba(12,153,129,0.12)] border border-[rgba(84,153,43,0.24)]">
                  {t('subscription.memberDetail.historyPrivacy')}
                </p>
              </div>
            )}

            {canAct && (
              <button
                className="text-center text-[14px] text-text-secondary font-medium py-2 mt-1 disabled:opacity-60"
                disabled={requestExitM.isPending}
                onClick={() => requestExitM.mutate()}
              >
                {requestExitM.isSuccess
                  ? t('subscription.memberDetail.exitSent')
                  : t('subscription.memberDetail.requestExit')}
              </button>
            )}
          </div>
        </Page>
        <MarkPaidSheet
          open={payOpen}
          subscription={sub}
          amountDue={viewerDebt}
          onClose={() => setPayOpen(false)}
          onRecorded={() => {
            void qc.invalidateQueries({ queryKey: ['subscription', id] });
            void qc.invalidateQueries({ queryKey: ['subscription-operations', id, 'viewer'] });
          }}
          onShowBalance={() => setPayOpen(false)}
        />
      </>
    );
  }

  return (
    <Page>
      {/* Header: back + icon + name + status badge */}
      <PageHeader
        title={
          <>
            <SubIcon sub={sub} />
            <b className="text-[19px] tracking-[-0.01em] truncate">{sub.name}</b>
          </>
        }
        right={
          <StatusBadge
            variant={statusBadgeVariant(sub.status)}
            label={statusBadgeLabel(sub.status, t)}
          />
        }
      />

      <div className="px-[18px] pb-8 flex flex-col gap-[13px]">
        {/* Holder view */}
        {is_holder && (
          <>
            <HolderSummaryCard sub={sub} memberCount={members.length} />
            <ChipRow subscriptionId={id!} status={sub.status} />
            <InviteLinkCard
              subscriptionId={id!}
              invite={invite}
              onRevoke={() => qc.invalidateQueries({ queryKey: ['subscription-invite', id] })}
            />
          </>
        )}

        {/* Tab switcher */}
        <TabBar active={tab} onChange={setTab} />

        {/* Members tab */}
        {tab === 'members' && (
          <div className="flex flex-col gap-[9px]">
            {membersQ.isLoading && (
              <div className="flex flex-col gap-2 animate-pulse">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="h-[48px] rounded-[15px] bg-white/40" />
                ))}
              </div>
            )}
            {!membersQ.isLoading && members.length === 0 && (
              <p className="text-center text-[14px] text-text-secondary py-8">
                {t('subscription.membersEmpty')}
              </p>
            )}
            {members.map((m) => (
              <MemberRow
                key={m.membership_id}
                member={m}
                isCurrentUser={false}
                onClick={is_holder ? () => navigate(`/subscriptions/${id}/members/${m.membership_id}`) : undefined}
              />
            ))}
          </div>
        )}

        {/* Finance tab */}
        {tab === 'finance' && (
          <div>
            {operationsQ.isLoading && <InlineState text={t('operation.loading')} />}
            {operationsQ.isError && (
              <InlineState text={t('operation.loadError')} onRetry={() => { void operationsQ.refetch(); }} />
            )}
            {!operationsQ.isLoading && !operationsQ.isError && operations.length === 0 && (
              <InlineState text={t('operation.empty')} />
            )}
            {operations.length > 0 && (
              <ListPanel>
                {operations.map((op) => <OperationRow key={op.id} op={op} />)}
              </ListPanel>
            )}
          </div>
        )}

        {/* Events tab */}
        {tab === 'events' && (
          <div className="flex flex-col gap-2">
            {is_holder && (
              <p className="text-[12px] text-text-secondary">
                {t('event.holderHint')}
              </p>
            )}
            {historyQ.isLoading && <InlineState text={t('event.loading')} />}
            {historyQ.isError && (
              <InlineState text={t('event.loadError')} onRetry={() => { void historyQ.refetch(); }} />
            )}
            {!historyQ.isLoading && !historyQ.isError && events.length === 0 && (
              <InlineState text={t('event.empty')} />
            )}
            {events.length > 0 && (
              <ListPanel>
                {events.map((event) => <EventRow key={event.id} event={event} />)}
              </ListPanel>
            )}
          </div>
        )}

        {/* Close subscription link (holder only) */}
        {is_holder && sub.status === 'active' && (
          <>
            <div className="flex-1" />
            <button
              className="text-center text-[14px] text-status-debt font-medium py-1 mt-2"
              onClick={() => navigate(`/subscriptions/${id}/close`)}
            >
              {t('subscription.actions.close')}
            </button>
          </>
        )}
      </div>
    </Page>
  );
}
